import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, TemplateRef, ViewChild } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { ClientSideRowModelModule, ColumnApiModule, DateFilterModule, GridApi, GridReadyEvent, ModuleRegistry, NumberFilterModule, PaginationModule, RenderApiModule, RowDragModule, RowSelectionModule, TextFilterModule, TooltipModule, ValidationModule } from 'ag-grid-community';
import { BsModalRef, BsModalService, ModalModule } from 'ngx-bootstrap/modal';
import { ToastrModule, ToastrService } from 'ngx-toastr';
import { ConfirmDialogComponent } from '../reusable/confirm-dialog/confirm-dialog.component';
import { ApiService } from '../../services/api.service';
import { CreateGlobalTagComponent } from './create-global-tag/create-global-tag.component';
import { CreateOrganisationComponent } from '../manage-organisation/create-organisation/create-organisation.component';
import { CreateBranchComponent } from '../manage-branches/create-branch/create-branch.component';
import { CreateUserComponent } from '../manage-user/create-user/create-user.component';
import { ToggleRendererComponent } from '../reusable/toggle-reusable/toggle-reusable.component';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  PaginationModule,
  RowSelectionModule,
  RowDragModule,
  TooltipModule,
  ColumnApiModule,
  ValidationModule
]);

@Component({
  selector: 'app-manage-global-tag',
  imports: [CommonModule, AgGridAngular, ModalModule, ToastrModule, ConfirmDialogComponent],
  templateUrl: './manage-global-tag.component.html',
  styleUrl: './manage-global-tag.component.scss',
  providers: [BsModalService]
})
export class ManageGlobalTagComponent {
  @ViewChild('cellTemplate', { static: true }) cellTemplate!: TemplateRef<any>;
  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;
  @ViewChild(ConfirmDialogComponent) confirmDialog!: ConfirmDialogComponent;
  gridApi!: GridApi;
  modalRef?: BsModalRef;
  selectedRowData: any;
  rowData: any[] = [];
  columnDefs: any = [
    {
      field: 'id', hide: true
    },
    {
      headerName: 'Tag Name', field: 'tag_name', tooltipField: 'tag_name', tooltipValueGetter: (params: any) => params.value || 'No data', flex: 1, sortable: true
    },
    {
      headerName: 'Description',
      field: 'description',
      tooltipValueGetter: (params: any) => this.stripHtml(params.value) || 'No data',
      valueFormatter: (params: any) => this.stripHtml(params.value),
      flex: 1
    },
    {
      headerName: 'Section Input',
      field: 'section_input',
      tooltipValueGetter: (params: any) => this.formatSectionInput(params.value) || 'No data',
      valueFormatter: (params: any) => this.formatSectionInput(params.value),
      flex: 1
    },
    {
      headerName: 'Deleted',
      field: 'deleted',
      cellRenderer: ToggleRendererComponent,
      flex: 1,
      sortable: false,
      filter: false,
    },
    {
      headerName: 'Actions',
      field: 'actions',
      cellRenderer: (params: any) => {
        return this.getHtml(params);
      },
      sortable: false,
      filter: false,
      flex: 1
    }
  ];
  modules = [ClientSideRowModelModule, ValidationModule, PaginationModule, RowSelectionModule, RowDragModule, TooltipModule, RenderApiModule, ColumnApiModule];
  pagination = true;
  paginationPageSizeSelector = [5, 10, 25, 50, 100];
  paginationPageSize = 10;

  gridOptions = {
    suppressMovableColumns: false,
    rowDragManaged: false,
    animateRows: true,
    enableBrowserTooltips: true,
  };

  constructor(
    private apiService: ApiService,
    private modalService: BsModalService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {

    this.getGlobalTags();
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.gridApi.setGridOption('context', { componentParent: this });
    this.gridApi.applyColumnState({
      state: [{ colId: 'id', sort: 'desc' }],
      defaultState: { sort: null },
    });
    //  this.addPageSizeSelectorToFooter();
  }
  editRow(rowData: any) {
    console.log('rowData', rowData);
    this.openModal(rowData);
  }
  deleteRow(rowData: any) {
    this.selectedRowData = rowData;
    this.confirmDialog.openModal(rowData);

  }

  onConfirmDelete() {
    if (!this.selectedRowData) return;
    this.apiService.delete(`globalTag/${this.confirmDialog.rowData.id}`).subscribe({
      next: (res: any) => {
        if(res.statusCode == 409){
          this.toastr.error(res.message, 'Error');
        }
        else{
          this.toastr.success('Global tag deleted successfully!', 'Success');
        }
        this.getGlobalTags();
      },
      error: (err: any) => {
        console.error('Error deleting global tag:', err);
        this.toastr.error(err.error?.message || 'Failed to delete global tag.', 'Error');
      }
    });
  }
  openModal(rowData = '') {
    this.modalRef = this.modalService.show(CreateGlobalTagComponent, {
      class: 'modal-lg my-custom-class',
      initialState: {
        globalTagData: rowData,
        parentPage: 'Manage Global Tag',
      }
    });
    (this.modalRef.content as CreateGlobalTagComponent).modalClosed.subscribe(() => {
      console.log('Modal Closed now refresh the list');
      this.getGlobalTags();
    });
  }

  getHtml(params: any) {
    const view = this.cellTemplate.createEmbeddedView({
      rowData: params.data,
    });
    view.detectChanges();
    const div = document.createElement('div');
    if (view.rootNodes.length > 0) {
      div.appendChild(view.rootNodes[0]);
      return div;
    } else {
      console.error('view.rootNodes is empty');
      return '';
    }
  }

  openOrganisationModal() {
    this.modalRef = this.modalService.show(CreateOrganisationComponent, {
      class: 'modal-lg my-custom-class',
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: false,
      animated: true,
      initialState: {
        parentPage: 'Manage Global Tag',
      }
    });
  }

  getGlobalTags(): void {
    this.apiService.get('globalTag').subscribe({
      next: (res: any) => {
        this.rowData = res.data;
        if (this.agGrid) {
          this.agGrid.api.refreshCells({ force: true });
        }
      },
      error: (err) => {
        console.log(err);
      }
    })
  }

  private stripHtml(value: string | null | undefined): string {
    return value ? value.replace(/<[^>]*>/g, '').trim() : '';
  }

  private formatSectionInput(value: Record<string, string> | null | undefined): string {
    if (!value || typeof value !== 'object') {
      return '';
    }

    return Object.entries(value)
      .map(([key, itemValue]) => `${key}: ${itemValue}`)
      .join(', ');
  }

  openBranchModal() {
    this.modalRef = this.modalService.show(CreateBranchComponent, {
      class: 'modal-lg my-custom-class',
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: false,
      animated: true,
      initialState: {
        parentPage: 'Manage Global Tag',
      }
    });
  }

  openUserModal() {
    this.modalRef = this.modalService.show(CreateUserComponent, {
      class: 'modal-lg my-custom-class',
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: false,
      animated: true,
      initialState: {
        parentPage: 'Manage Global Tag',
      }
    });
  }

   onStatusToggle(rowData: any, newStatus: boolean): void {
    const updatedGlobalTag = { ...rowData, deleted: newStatus };
    this.apiService.patch(`GlobalTag/update-status/${rowData.id}`, updatedGlobalTag).subscribe({
      next: () => {
        this.toastr.success('Global tag status updated successfully!', 'Success');
        this.getGlobalTags();
      },
      error: (err) => {
        console.error('Error updating global tag status:', err);
        this.toastr.error(err.error?.message || 'Failed to update global tag status.', 'Error');
       
        this.getGlobalTags();
      }
    });
  }
}
